import { useState, useEffect } from 'react';
import { createChannel, updateChannel } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_VISIBILITY = {
  show_unit_numbers: true,
  show_pricing: true,
  show_total_count: true,
  allow_soft_block: true,
  allow_booking: false
};

export const ChannelFormDialog = ({ isOpen, onOpenChange, editingChannel, developerId, onSaved }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    channel_type: 'internal',
    description: '',
    developer_id: developerId,
    visibility: { ...DEFAULT_VISIBILITY }
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (editingChannel) {
      setFormData({
        name: editingChannel.name,
        code: editingChannel.code,
        channel_type: editingChannel.channel_type,
        description: editingChannel.description || '',
        developer_id: editingChannel.developer_id,
        visibility: editingChannel.visibility || { ...DEFAULT_VISIBILITY }
      });
    } else {
      setFormData({
        name: '',
        code: '',
        channel_type: 'internal',
        description: '',
        developer_id: developerId,
        visibility: { ...DEFAULT_VISIBILITY }
      });
    }
  }, [isOpen, editingChannel, developerId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingChannel) {
        await updateChannel(editingChannel.channel_id, formData);
        toast.success('Channel updated successfully');
      } else {
        await createChannel(formData);
        toast.success('Channel created successfully');
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      console.error('Failed to save channel:', error);
      toast.error(error.message || 'Failed to save channel');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{editingChannel ? 'Edit Channel' : 'Create New Channel'}</DialogTitle>
          <DialogDescription>
            {editingChannel ? 'Update channel settings and visibility rules' : 'Add a new sales channel'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="channel-name">Channel Name</Label>
              <Input
                id="channel-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Website Sales"
                required
                data-testid="channel-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="channel-code">Channel Code</Label>
              <Input
                id="channel-code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="WEB001"
                required
                data-testid="channel-code-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Channel Type</Label>
            <Select 
              value={formData.channel_type} 
              onValueChange={(v) => setFormData({ ...formData, channel_type: v })}
            >
              <SelectTrigger data-testid="channel-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="internal">Internal (Sales Team)</SelectItem>
                <SelectItem value="partner">Partner (Channel Partners)</SelectItem>
                <SelectItem value="public">Public (Website/App)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="channel-description">Description</Label>
            <Input
              id="channel-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Main website sales channel"
              data-testid="channel-description-input"
            />
          </div>
          
          {/* Visibility Rules */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Visibility Rules</h4>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Show Unit Numbers</Label>
                <p className="text-xs text-muted-foreground">Display actual unit numbers</p>
              </div>
              <Switch
                checked={formData.visibility.show_unit_numbers}
                onCheckedChange={(v) => setFormData({ 
                  ...formData, 
                  visibility: { ...formData.visibility, show_unit_numbers: v }
                })}
                data-testid="show-unit-numbers-switch"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Show Pricing</Label>
                <p className="text-xs text-muted-foreground">Display unit prices</p>
              </div>
              <Switch
                checked={formData.visibility.show_pricing}
                onCheckedChange={(v) => setFormData({ 
                  ...formData, 
                  visibility: { ...formData.visibility, show_pricing: v }
                })}
                data-testid="show-pricing-switch"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Allow Soft Block</Label>
                <p className="text-xs text-muted-foreground">Can temporarily block units</p>
              </div>
              <Switch
                checked={formData.visibility.allow_soft_block}
                onCheckedChange={(v) => setFormData({ 
                  ...formData, 
                  visibility: { ...formData.visibility, allow_soft_block: v }
                })}
                data-testid="allow-soft-block-switch"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Allow Booking</Label>
                <p className="text-xs text-muted-foreground">Can confirm bookings</p>
              </div>
              <Switch
                checked={formData.visibility.allow_booking}
                onCheckedChange={(v) => setFormData({ 
                  ...formData, 
                  visibility: { ...formData.visibility, allow_booking: v }
                })}
                data-testid="allow-booking-switch"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid="save-channel-btn">
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingChannel ? 'Update Channel' : 'Create Channel'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
